import { Tag, FormInstance } from "antd";
import { Dayjs } from "dayjs";
import { QueryOperation, SearchQuery } from "../../code/search-query/search-query";
import { buildSearchQuery, comboboxValue, dateRangeValue, QueryFieldMapping } from "../../code/search-query/query-mapper";
import { DataColumn } from "../../controls/Data.default";
import { DynamicSchema } from "../../controls/DynamicForm.default";
import { InputObject } from "../../controls/default";
import {
  IncidentSeverityType,
  IncidentStatusType,
} from "../../repository/data.default";
import {
  createSearchRequest,
  createScopedFilterRequest,
} from "../../hooks/useSearchRequest";
import {
  getCompanies,
  getIncidentTypes,
  getCompanyDepartments,
  getPlatformUsers,
} from "../../repository/data.repository";
import { createStatusColorMap } from "../../utils/colors";

export interface IncidentsViewForm {
  readonly title: string;
  readonly description: string;
  readonly reportedDate: Dayjs[];
  readonly status: InputObject<IncidentStatusType>;
  readonly severity: InputObject<IncidentSeverityType>;
  readonly assignedToUserUserName: string;
  readonly company: InputObject;
  readonly department: InputObject;
  readonly assignedToUser: InputObject;
  readonly incidentType: InputObject;
}

export const incidentsViewColumns: DataColumn[] = [
  {
    title: "Title",
    key: "title",
    width: 192,
    sort: true,
    defaultSort: "asc",
  },
  {
    title: "Description",
    key: "description",
    width: 192,
    sort: true,
  },
  {
    title: "Reported Date",
    key: "reportedDate",
    width: 192,
    sort: true,
    format: "DD/MM/YYYY HH:mm",
  },
  {
    title: "Type",
    key: "incidentTypeName",
    width: 192,
    sort: true,
  },
  {
    title: "Status",
    key: "status",
    width: 140,
    sort: true,
    // Colored Tag to match the Manage view's status banner (mirrors ReportsView/CounsellingRequestsView).
    transform: (value: string) => (
      <Tag color={incidentStatusColor(value as IncidentStatusType)}>{value}</Tag>
    ),
  },
  {
    title: "Severity",
    key: "severity",
    width: 140,
    sort: true,
    transform: (value: string) => (
      <Tag color={incidentSeverityColor(value as IncidentSeverityType)}>{value}</Tag>
    ),
  },
  {
    title: "Reported By",
    key: "reportedByUserUserName",
    width: 192,
    sort: true,
  },
  {
    title: "Assigned To User",
    key: "assignedToUserUserName",
    width: 192,
    sort: true,
  },
  {
    title: "Company",
    key: "companyName",
    width: 192,
    sort: true,
  },
  {
    title: "Department",
    key: "departmentName",
    width: 192,
    sort: true,
  },
  {
    title: "Comments",
    key: "commentsCount",
    width: 192,
    sort: true,
  },
];

const incidentsQueryMappings: QueryFieldMapping[] = [
  { formField: "company", queryField: "companyUid", operation: QueryOperation.Equal, transform: comboboxValue },
  { formField: "title" },
  { formField: "description" },
  { formField: "reportedDate", operation: QueryOperation.Between, transform: dateRangeValue },
  { formField: "status", operation: QueryOperation.Equal, transform: comboboxValue },
  { formField: "severity", operation: QueryOperation.Equal, transform: comboboxValue },
  { formField: "assignedToUser", queryField: "assignedToUserUid", operation: QueryOperation.Equal, transform: comboboxValue },
  { formField: "department", queryField: "departmentUid", operation: QueryOperation.Equal, transform: comboboxValue },
  { formField: "incidentType", queryField: "incidentTypeUid", operation: QueryOperation.Equal, transform: comboboxValue },
];

export const mapIncientsViewQuery = (values: IncidentsViewForm): SearchQuery =>
  buildSearchQuery(values, incidentsQueryMappings);

// Factory: Department + Assigned-User scope to the selected Company. **`dependsOn: ["company"]` is the fix** —
// previously these were scoped inline but without it, so a stale Department/Assignee survived a Company change.
export const incidentsSearchSchema = (form: FormInstance<any>): DynamicSchema => ({
  groups: [
    {
      controls: [
        { type: "combobox", label: "Company", name: "company", request: createSearchRequest(getCompanies, "name") },
        { type: "combobox", label: "Type", name: "incidentType", request: createSearchRequest(getIncidentTypes, "name") },
        { type: "textbox", label: "Title", name: "title" },
        { type: "textbox", label: "Description", name: "description" },
        { type: "rangetimepicker", label: "Reported Date", name: "reportedDate" },
        {
          type: "combobox",
          label: "Status",
          name: "status",
          request: () =>
            Promise.resolve(
              Object.entries(IncidentStatusType).map(([value, display]) => ({ display, value })),
            ),
        },
        {
          type: "combobox",
          label: "Severity",
          name: "severity",
          request: () =>
            Promise.resolve(
              Object.entries(IncidentSeverityType).map(([value, display]) => ({ display, value })),
            ),
        },
        {
          type: "combobox",
          label: "Department",
          name: "department",
          dependsOn: ["company"],
          request: createScopedFilterRequest(getCompanyDepartments, "name", () => [
            ["companyUid", form.getFieldValue("company")?.value],
          ]),
        },
        {
          type: "combobox",
          label: "Assigned User",
          name: "assignedToUser",
          dependsOn: ["company"],
          request: createScopedFilterRequest(getPlatformUsers, "userName", () => [
            ["companyUid", form.getFieldValue("company")?.value],
          ]),
        },
      ],
    },
  ],
});

// Status → Tag color (shared by the grid and the Manage view).
export const incidentStatusColor = createStatusColorMap<IncidentStatusType>({
  [IncidentStatusType.Open]: "blue",
  [IncidentStatusType.InProgress]: "gold",
  [IncidentStatusType.Resolved]: "green",
  [IncidentStatusType.Closed]: "default",
});

// Severity → Tag color, escalating warm hues toward Critical.
export const incidentSeverityColor = createStatusColorMap<IncidentSeverityType>({
  [IncidentSeverityType.Low]: "green",
  [IncidentSeverityType.Medium]: "gold",
  [IncidentSeverityType.High]: "orange",
  [IncidentSeverityType.Critical]: "red",
});

// One-line guidance: what the ticket is waiting on at each status.
export const incidentStatusGuidance = (status?: IncidentStatusType): string => {
  switch (status) {
    case IncidentStatusType.Open:
      return "Newly reported — triage it, assign an owner and start working it.";
    case IncidentStatusType.InProgress:
      return "Being worked — resolve it once the issue is addressed.";
    case IncidentStatusType.Resolved:
      return "Resolved — close it to finish, or reopen if it recurs.";
    case IncidentStatusType.Closed:
      return "This incident is closed. Reopen it if it needs more work.";
    default:
      return "";
  }
};

// Allowed status transitions — mirrors the backend state machine (IncidentsController), so the
// Status picker only offers legal moves and we never round-trip a 400.
export const incidentAllowedTransitions: Record<
  IncidentStatusType,
  IncidentStatusType[]
> = {
  [IncidentStatusType.Open]: [
    IncidentStatusType.InProgress,
    IncidentStatusType.Resolved,
    IncidentStatusType.Closed,
  ],
  [IncidentStatusType.InProgress]: [
    IncidentStatusType.Resolved,
    IncidentStatusType.Closed,
    IncidentStatusType.Open,
  ],
  [IncidentStatusType.Resolved]: [
    IncidentStatusType.Closed,
    IncidentStatusType.InProgress,
  ],
  [IncidentStatusType.Closed]: [IncidentStatusType.InProgress],
};
